import { BillerModel } from 'app/biller/models/BillerModel';
import { BusinessUnitType } from 'app/business/services/useBusinessUnit';
import { DomicilioModel } from 'app/domicilio/models/DomicilioModel';
import moment, { Moment } from 'moment';
import { domicilioInitialValues } from 'shared/components/FormikAddress';
import { VehiculoPartialModel } from '../../vehiculos/models/Vehiculo';
import { ChoferCreateModel, ChoferCRUD, ChoferModel, Turno } from './Chofer';
import { ChoferCategoria } from './ChoferCategoria';
import { ChoferIdioma } from './ChoferIdioma';

export interface ChoferCreateFormSchema {
  general: ChoferGeneralFormSchema;
  personal: ChoferPersonalFormSchema;
  vencimientos: ChoferVencimientosFormSchema;
}
export interface ChoferFormSchema {
  general: ChoferGeneralFormSchema;
  personal: ChoferPersonalFormSchema;
  turno: ChoferTurnoFormSchema;
  vencimientos: ChoferVencimientosFormSchema;
}

export interface ChoferFormSchemaFromBack {
  general: ChoferGeneralFormSchema;
  personal: ChoferPersonalFormSchema;
  turno: ChoferTurnoFormSchema | null;
  vencimientos: ChoferVencimientosFormSchema;
}

export interface ChoferGeneralFormSchema {
  codigo: number | string;
  nombreCompleto: string;
  proveedores: BillerModel[];
  vehiculo: VehiculoPartialModel;
  categoria: ChoferCategoria;
  tienePosnet: boolean;
  tieneCursoDeDefensa: boolean;
  fechaIngreso: Moment;
  comision: number;
  businessUnit?: BusinessUnitType | null;
}

export interface ChoferPersonalFormSchema {
  foto: File | string;
  dni_front_photo: File | string;
  dni_back_photo: File | string;
  fechaNacimiento: Moment;
  dni: string | number;
  cuit: string | number;
  domicilio: DomicilioModel;
  telefonoEmergencia: string | number;
  celular: string | number;
  mail: string;
  idiomas: ChoferIdioma[];
}

export interface ChoferTurnoFormSchema {
  lunes: Turno[];
  martes: Turno[];
  miercoles: Turno[];
  jueves: Turno[];
  viernes: Turno[];
  sabado: Turno[];
  domingo: Turno[];
}

export interface ChoferVencimientosFormSchema {
  fechaLicencia: Moment;
  licence_front_photo: File | string;
  licence_back_photo: File | string;
  fotoLinti?: File | string;
  vencimientoLinti?: Moment;
  fotoExamenMedico?: File | string;
  vencimientoExamenMedico?: Moment;
  fotoLibretaSanitaria?: File | string;
  vencimientoLibretaSanitaria?: Moment;
}

export function choferModelToFormSchema({
  general,
  personal,
  turno,
  vencimientos
}: ChoferModel): ChoferFormSchema {
  return {
    general: {
      ...general,
      tieneCursoDeDefensa: personal.tieneCursoDeDefensa,
      vehiculo: general.vehiculo
    },
    personal: {
      ...personal,
      domicilio: personal.domicilio
    },
    turno: turno? {...turno}: choferTurnoFormSchemaInitialValues(personal.domicilio),
    vencimientos: {
      fechaLicencia: vencimientos.fechaLicencia,
      licence_back_photo: vencimientos.licence_back_photo,
      licence_front_photo: vencimientos.licence_front_photo,
      ...(vencimientos.vencimientoLinti
        ? {
            vencimientoLinti: vencimientos.vencimientoLinti
          }
        : {}),
      ...( vencimientos.fotoLinti
        ? {
            fotoLinti: vencimientos.fotoLinti,
          }
        : {}),
      ...(vencimientos.vencimientoExamenMedico
        ? {
            vencimientoExamenMedico: vencimientos.vencimientoExamenMedico
          }
        : {}),
        ...(vencimientos.fotoExamenMedico
          ? {
              fotoExamenMedico: vencimientos.fotoExamenMedico,
            }
          : {}),
      ...(vencimientos.vencimientoLibretaSanitaria
        ? {
            vencimientoLibretaSanitaria: vencimientos.vencimientoLibretaSanitaria
          }
        : {}),
      ...(vencimientos.fotoLibretaSanitaria
        ? {
            fotoLibretaSanitaria: vencimientos.fotoLibretaSanitaria,
          }
        : {})
    }
  };
}

export function choferCreateFormSchemaToModel({
  general,
  personal,
  vencimientos
}: ChoferCreateFormSchema): ChoferCreateModel {
  return {
    general: {
      ...general,
      codigo: Number(general.codigo),
      businessUnit: general.businessUnit
    },
    personal: {
      ...personal,
      domicilio: personal.domicilio,
      tieneCursoDeDefensa: general.tieneCursoDeDefensa,
      dni: Number(personal.dni),
      cuit: Number(personal.cuit)
    },
    vencimientos: {
      fechaLicencia: vencimientos.fechaLicencia,
      licence_back_photo: vencimientos.licence_back_photo,
      licence_front_photo: vencimientos.licence_front_photo,
      ...(vencimientos.vencimientoLinti && vencimientos.fotoLinti
        ? {
            fotoLinti: vencimientos.fotoLinti,
            vencimientoLinti: vencimientos.vencimientoLinti
          }
        : {}),
      ...(vencimientos.vencimientoExamenMedico && vencimientos.fotoExamenMedico
        ? {
            fotoExamenMedico: vencimientos.fotoExamenMedico,
            vencimientoExamenMedico: vencimientos.vencimientoExamenMedico
          }
        : {}),
      ...(vencimientos.vencimientoLibretaSanitaria &&
      vencimientos.fotoLibretaSanitaria
        ? {
            fotoLibretaSanitaria: vencimientos.fotoLibretaSanitaria,
            vencimientoLibretaSanitaria:
              vencimientos.vencimientoLibretaSanitaria
          }
        : {})
    }
  };
}
export function choferFormSchemaToModel({
  general,
  personal,
  turno,
  vencimientos
}: ChoferFormSchema): ChoferCRUD {
  return {
    general: {
      ...general,
      codigo: Number(general.codigo),
      businessUnit: general.businessUnit
    },
    personal: {
      ...personal,
      domicilio: personal.domicilio,
      tieneCursoDeDefensa: general.tieneCursoDeDefensa,
      dni: Number(personal.dni),
      cuit: Number(personal.cuit)
    },
    turno: {
      ...turno,
    },
    vencimientos: {
      fechaLicencia: vencimientos.fechaLicencia,
      licence_back_photo: vencimientos.licence_back_photo,
      licence_front_photo: vencimientos.licence_front_photo,
      ...(vencimientos.vencimientoLinti && vencimientos.fotoLinti
        ? {
            fotoLinti: vencimientos.fotoLinti,
            vencimientoLinti: vencimientos.vencimientoLinti
          }
        : {}),
      ...(vencimientos.vencimientoExamenMedico && vencimientos.fotoExamenMedico
        ? {
            fotoExamenMedico: vencimientos.fotoExamenMedico,
            vencimientoExamenMedico: vencimientos.vencimientoExamenMedico
          }
        : {}),
      ...(vencimientos.vencimientoLibretaSanitaria &&
      vencimientos.fotoLibretaSanitaria
        ? {
            fotoLibretaSanitaria: vencimientos.fotoLibretaSanitaria,
            vencimientoLibretaSanitaria:
              vencimientos.vencimientoLibretaSanitaria
          }
        : {})
    }
  };
}

export function choferGeneralFormSchemaInitialValues(
  vehiculos: VehiculoPartialModel[],
  proveedores: BillerModel[]
): ChoferGeneralFormSchema {
  return {
    codigo: '',
    nombreCompleto: '',
    proveedores: [],
    vehiculo: vehiculos[0],
    categoria: ChoferCategoria.BRONCE,
    tienePosnet: false,
    tieneCursoDeDefensa: false,
    fechaIngreso: moment(),
    comision: 25
  };
}

export function choferPersonalFormSchemaInitialValues(): ChoferPersonalFormSchema {
  return {
    foto: '',
    dni_front_photo: '',
    dni_back_photo: '',
    fechaNacimiento: moment(),
    dni: '',
    cuit: '',
    domicilio: domicilioInitialValues,
    telefonoEmergencia: '',
    celular: '',
    mail: '',
    idiomas: []
  };
}

export const initialTurn = (domicilio?: DomicilioModel): Turno => {
  const horaInicio = moment()
  const horaFin = horaInicio.clone().add(4, 'hours')
  return{
  id: 0,
  dia: '',
  desde: horaInicio,
  hasta: horaFin,
  id_conductor: 0,
  creado_en: '',
  actualizado_en: '',
  direccion: domicilio || {
    latitud: 0,
    longitud: 0,
    calle: '',
    numero: '',
    partido: '',
    provincia: '',
    departamento: '',
    localidad: '',
    piso: '',
  }
}};



export function choferTurnoFormSchemaInitialValues(domicilio?: DomicilioModel): ChoferTurnoFormSchema {
  return {
    lunes: [initialTurn(domicilio)],
    martes: [initialTurn(domicilio)],
    miercoles: [initialTurn(domicilio)],
    jueves: [initialTurn(domicilio)],
    viernes: [initialTurn(domicilio)],
    sabado: [initialTurn(domicilio)], 
    domingo: [initialTurn(domicilio)],
  };
}

export function choferVencimientoFormSchemaInitialValues(): ChoferVencimientosFormSchema {
  return {
    fechaLicencia: moment(),
    licence_front_photo: '',
    licence_back_photo: '',
    fotoLinti: '',
    vencimientoLinti: moment(),
    fotoExamenMedico: '',
    vencimientoExamenMedico: moment(),
    fotoLibretaSanitaria: '',
    vencimientoLibretaSanitaria: moment()
  };
}

export function choferFormSchemaInitialValues(
  vehiculos: VehiculoPartialModel[],
  proveedores: BillerModel[]
) {
  return {
    general: choferGeneralFormSchemaInitialValues(vehiculos, proveedores),
    personal: choferPersonalFormSchemaInitialValues(),
    vencimientos: choferVencimientoFormSchemaInitialValues()
  };
}
